import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GoogleSignupInput, LoginInput, SessionResponse, VerifySignupInput } from '@worki/shared';
import { api, RequestError, setUnauthorizedHandler } from './api.js';

/**
 * What a finished sign-up answers with. `{ pending: true }` is the approval gate:
 * the account exists and Worki has not let it in, so the answer has no user in it
 * and no cookie behind it. It is something for the page to show, not a session.
 */
export type SignupAnswer = SessionResponse | { pending: true };

/** Only an answer with a user in it is a session. */
export const isAdmitted = (answer: SignupAnswer): answer is SessionResponse => 'user' in answer;

interface SessionContextValue {
  session: SessionResponse | null;
  loading: boolean;
  /**
   * True only when the server could not be reached at all. Distinguishing this
   * from "signed out" matters: a 401 means log in again, but a dead socket (API
   * restarting, laptop asleep) must not throw away a perfectly valid cookie.
   */
  unreachable: boolean;
  login: (input: LoginInput) => Promise<SessionResponse>;
  /** Step 2 of sign-up. Step 1 (`POST /auth/signup`) only emails a code and returns no session. */
  verifySignup: (input: VerifySignupInput) => Promise<SignupAnswer>;
  /**
   * Step 2 of the Google sign-up. Google proved the address on the way back
   * from its own screen; this carries the business details it could not know.
   */
  completeGoogleSignup: (input: GoogleSignupInput) => Promise<SignupAnswer>;
  logout: () => Promise<void>;
  refresh: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        return await api.get<SessionResponse>('/auth/me');
      } catch (error) {
        // A 401 is the normal signed-out state, not a failure to report.
        if (error instanceof RequestError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 5 * 60_000,
  });

  const loginMutation = useMutation({
    mutationFn: (input: LoginInput) => api.post<SessionResponse>('/auth/login', input),
    onSuccess: (data) => queryClient.setQueryData(['session'], data),
  });

  /*
   * A waiting account is handed back to the page and not written here. The route
   * guards take anything in this slot for a signed-in user, so `{ pending: true }`
   * sent `/login` to a dashboard that could not draw — and, after approval, left
   * no way back to the login screen until the page was reloaded.
   */
  const rememberIfAdmitted = (data: SignupAnswer) => {
    if (isAdmitted(data)) queryClient.setQueryData(['session'], data);
  };

  const verifySignupMutation = useMutation({
    mutationFn: (input: VerifySignupInput) => api.post<SignupAnswer>('/auth/verify-signup', input),
    onSuccess: rememberIfAdmitted,
  });

  const googleSignupMutation = useMutation({
    mutationFn: (input: GoogleSignupInput) => api.post<SignupAnswer>('/auth/google/complete', input),
    onSuccess: rememberIfAdmitted,
  });

  /**
   * Drops the signed-in state: the session becomes a definite `null`, and every
   * other cached query goes, because nothing in it belongs to the next user.
   *
   * The order is not cosmetic. `queryClient.clear()` deletes the query object
   * this provider's `useQuery` is bound to, and writing the key again builds a
   * *new* one — the mounted observer stays attached to the deleted query and
   * keeps reporting the old session until something else happens to re-render.
   * That is a signed-out user still looking at their own screen. So the session
   * entry is written first, which notifies the live observer, and the sweep
   * afterwards spares that one key.
   */
  const forget = useCallback(() => {
    queryClient.setQueryData(['session'], null);
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'session' });
  }, [queryClient]);

  const logout = useCallback(async () => {
    await api.post('/auth/logout');
    forget();
  }, [forget]);

  /**
   * A session can stop resolving while the user is still on a signed-in screen —
   * the cookie stays cryptographically valid but the account it points at is
   * gone. Without this, every query on the page just retries 401 forever behind
   * a half-rendered screen; now the route guard sees no session and redirects.
   */
  useEffect(() => {
    setUnauthorizedHandler(forget);
  }, [forget]);

  const value = useMemo<SessionContextValue>(
    () => ({
      session: query.data ?? null,
      loading: query.isLoading,
      unreachable: query.isError,
      login: (input) => loginMutation.mutateAsync(input),
      verifySignup: (input) => verifySignupMutation.mutateAsync(input),
      completeGoogleSignup: (input) => googleSignupMutation.mutateAsync(input),
      logout,
      refresh: () => void queryClient.invalidateQueries({ queryKey: ['session'] }),
    }),
    [
      query.data,
      query.isLoading,
      query.isError,
      loginMutation,
      verifySignupMutation,
      googleSignupMutation,
      logout,
      queryClient,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside <SessionProvider>');
  return context;
}

/** Signed-in screens can assume a session exists; the route guard enforces it. */
export function useAuthenticated(): SessionResponse {
  const { session } = useSession();
  if (!session) throw new Error('useAuthenticated used outside a protected route');
  return session;
}
